// =============================================================================
// ONGLET LIAISON ANAH - Fiche Dossier OH
// =============================================================================
// Affiche la fiche ANAH liée au dossier OH (si liée)
// Permet de rechercher et lier un dossier ANAH

import { useState, useEffect, useCallback } from "react";
import {
  Building2,
  Search,
  Link,
  Unlink,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Euro,
  Calendar,
  FileText,
  User,
  MapPin,
  Phone,
  Hash,
  Info,
  Loader2,
  X,
  Edit3,
  Save
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { formatDateFR } from "../../utils/dateFormat";
import AnahReferencesBlock from "./AnahReferencesBlock";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Mapping des statuts vers couleurs
const STATUT_COLORS = {
  en_cours: { bg: "bg-[var(--oh-accent-bg)]", text: "text-[var(--oh-accent-text)]", border: "border-[var(--oh-accent)]" },
  déposé: { bg: "bg-[var(--oh-info-bg)]", text: "text-[var(--oh-info-text)]", border: "border-[var(--oh-info-border)]" },
  instruction: { bg: "bg-[var(--oh-accent-bg)]", text: "text-[var(--oh-accent-text)]", border: "border-[var(--oh-accent)]" },
  accorde: { bg: "bg-[var(--oh-success-bg)]", text: "text-[var(--oh-success-text)]", border: "border-[var(--oh-success-border)]" },
  refuse: { bg: "bg-[var(--oh-error-bg)]", text: "text-[var(--oh-error-text)]", border: "border-[var(--oh-error-border)]" },
  annule: { bg: "bg-[var(--oh-bg-elevated)]/20", text: "text-[var(--oh-text-secondary)]", border: "border-[var(--oh-border)]" },
  paye: { bg: "bg-[var(--oh-success-bg)]", text: "text-[var(--oh-success-text)]", border: "border-[var(--oh-success-border)]" },
  clôture: { bg: "bg-[var(--oh-bg-elevated)]/20", text: "text-[var(--oh-text-secondary)]", border: "border-[var(--oh-border)]" },
  inconnu: { bg: "bg-[var(--oh-bg-elevated)]/20", text: "text-[var(--oh-text-secondary)]", border: "border-[var(--oh-border)]" }
};

// Labels statuts
const STATUT_LABELS = {
  en_cours: "En cours",
  déposé: "Déposé",
  instruction: "En instruction",
  accorde: "Accordé",
  refuse: "Refusé",
  annule: "Annulé",
  paye: "Payé",
  clôture: "Clôturé",
  inconnu: "Inconnu"
};

export default function AnahLinkTab({ dossierId, tenantId, readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  
  // État de liaison
  const [anahStatus, setAnahStatus] = useState(null);
  
  // Modal de recherche
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Édition de la référence manuelle
  const [manualRef, setManualRef] = useState("");
  const [showManualInput, setShowManualInput] = useState(false);
  
  // Édition des informations complémentaires
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    const userId = localStorage.getItem('user_id');
    const userRole = localStorage.getItem('user_role');
    return {
      "X-Tenant-ID": tenantId || "tef",
      "Content-Type": "application/json",
      ...(userId && { "X-User-ID": userId }),
      ...(userRole && { "X-User-Role": userRole }),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  }, [tenantId]);

  // Charger le statut de liaison
  const loadAnahStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/anah-status`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setAnahStatus(data);
        setManualRef(data.anah_reference || "");
      } else if (res.status === 404) {
        setAnahStatus({ anah_reference: null, anah_linked: false, anah_data: null });
      }
    } catch (error) {
      console.error("Erreur chargement statut ANAH:", error);
    } finally {
      setLoading(false);
    }
  }, [dossierId, getAuthHeaders]);

  useEffect(() => {
    loadAnahStatus();
  }, [loadAnahStatus]);

  // Sauvegarder les informations complémentaires
  const handleSaveManualData = async () => {
    setSaving(true);
    try {
      if (anahStatus?.anah_reference) {
        // Sauvegarder via l'API ANAH si on a une référence
        const res = await fetch(`${API}/api/anah/dossiers/${anahStatus.anah_reference}/manual-data`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            subvention_manuelle: editData.subvention_manuelle ? parseFloat(editData.subvention_manuelle) : null,
            telephone: editData.telephone_manuel || null,
            notes: editData.notes || null,
            date_depot: editData.date_depot || null,
            date_acceptation: editData.date_acceptation || null,
            date_instruction: editData.date_instruction || null,
            date_fin_travaux: editData.date_fin_travaux || null,
            date_paiement: editData.date_paiement || null
          })
        });
        
        if (res.ok) {
          toast.success("Informations enregistrées");
          setIsEditing(false);
          loadAnahStatus();
        } else {
          const data = await res.json();
          toast.error(data.detail || "Erreur lors de l'enregistrement");
        }
      } else {
        // Sauvegarder directement sur le dossier si pas de liaison ANAH
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/aides-mpa-dates`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            date_depot: editData.date_depot || null,
            date_acceptation: editData.date_acceptation || null,
            date_instruction: editData.date_instruction || null,
            date_fin_travaux: editData.date_fin_travaux || null,
            date_paiement: editData.date_paiement || null,
            subvention_manuelle: editData.subvention_manuelle ? parseFloat(editData.subvention_manuelle) : null,
            telephone_manuel: editData.telephone_manuel || null,
            notes: editData.notes || null,
          })
        });
        
        if (res.ok) {
          toast.success("Informations enregistrées");
          setIsEditing(false);
          loadAnahStatus();
        } else {
          const data = await res.json().catch(() => ({}));
          toast.error(data.detail || "Erreur lors de l'enregistrement");
        }
      }
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  // Rechercher des dossiers ANAH
  const searchAnahDossiers = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/anah/search?q=${encodeURIComponent(searchQuery)}&limit=20`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      } else {
        toast.error("Erreur lors de la recherche");
      }
    } catch (error) {
      console.error("Erreur recherche ANAH:", error);
      toast.error("Erreur réseau");
    } finally {
      setSearching(false);
    }
  };

  // Lier un dossier ANAH
  const linkAnahDossier = async (anahReference) => {
    setLinking(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/anah-link`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ anah_reference: anahReference })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message || "Liaison établie");
        setShowSearchModal(false);
        setShowManualInput(false);
        loadAnahStatus();
      } else {
        toast.error(data.detail || "Erreur lors de la liaison");
      }
    } catch (error) {
      console.error("Erreur liaison ANAH:", error);
      toast.error("Erreur réseau");
    } finally {
      setLinking(false);
    }
  };

  // Délier le dossier ANAH
  const unlinkAnahDossier = async () => {
    if (!confirm("Voulez-vous vraiment supprimer la liaison avec ce dossier ANAH ?")) return;
    
    setLinking(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/anah-link`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ anah_reference: null })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success("Liaison supprimée");
        loadAnahStatus();
      } else {
        toast.error(data.detail || "Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur suppression liaison ANAH:", error);
      toast.error("Erreur réseau");
    } finally {
      setLinking(false);
    }
  };

  // Formater la date — délégué au helper central JJ/MM/AAAA
  const formatDate = formatDateFR;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--oh-success-text)]" />
        <span className="ml-2 text-[var(--oh-text-secondary)]">Chargement...</span>
      </div>
    );
  }

  const isLinked = anahStatus?.anah_linked;
  const anahData = anahStatus?.anah_data;
  const hasReference = !!anahStatus?.anah_reference;

  return (
    <div className="space-y-6" data-testid="anah-link-tab">
      {/* Bloc multi-références (S1 2026-05-28) */}
      <AnahReferencesBlock
        dossierId={dossierId}
        tenantId={tenantId}
        initialRefs={anahStatus?.anah_references || []}
        readOnly={readOnly}
        onUpdated={() => loadAnahStatus()}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[var(--oh-success-bg)] rounded-lg">
            <Building2 className="w-5 h-5 text-[var(--oh-success-text)]" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-[var(--oh-text)]">Liaison ANAH</h3>
            <p className="text-sm text-[var(--oh-text-secondary)]">
              Suivi du dossier MaPrimeAdapt' sur le portail ANAH
            </p>
          </div>
        </div>
        
        {!readOnly && (
          <div className="flex gap-2">
            {hasReference ? (
              <Button
                variant="outline"
                size="sm"
                onClick={unlinkAnahDossier}
                disabled={linking}
                className="border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]"
              >
                {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4 mr-1" />}
                Délier
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowManualInput(true)}
                  className="border-[var(--oh-border)] text-[var(--oh-text-secondary)]"
                >
                  <Hash className="w-4 h-4 mr-1" />
                  Saisir référence
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowSearchModal(true)}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Search className="w-4 h-4 mr-1" />
                  Rechercher
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Alertes / Issues */}
      {anahStatus?.issues?.length > 0 && (
        <Card className="bg-[var(--oh-info-bg)] border-[var(--oh-info-border)]">
          <CardContent className="p-4">
            {anahStatus.issues.map((issue, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[var(--oh-info-text)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[var(--oh-info-text)] font-medium">Attention</p>
                  <p className="text-[var(--oh-info-text)] text-sm">{issue.message}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Contenu principal */}
      {!hasReference ? (
        // Pas de liaison
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[var(--oh-bg-elevated)] flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-[var(--oh-text-secondary)]" />
              </div>
              <h4 className="text-lg font-medium text-[var(--oh-text)] mb-2">
                Aucun dossier ANAH lié
              </h4>
              <p className="text-[var(--oh-text-secondary)] max-w-md mx-auto">
                Ce dossier n'est pas encore lié à un dossier ANAH. 
                Vous pouvez rechercher et lier un dossier existant ou saisir manuellement la référence.
              </p>
              <p className="text-[var(--oh-text-secondary)] text-sm mt-4">
                <Info className="w-4 h-4 inline mr-1" />
                L'absence de liaison n'est pas une erreur si le dossier n'a pas encore été déposé sur ANAH.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : isLinked ? (
        // Dossier lié avec données
        <div className="space-y-4">
          {/* Carte principale avec statut */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[var(--oh-text)] flex items-center gap-2 flex-wrap">
                  <CheckCircle className="w-5 h-5 text-[var(--oh-success-text)]" />
                  Dossier ANAH lié
                  {/* CC-DEV1-19 #2 — indice discret : ces données (statut, dates de
                      dépôt, etc.) viennent de la sync ANAH et sont donc en lecture
                      seule (pas un bug). */}
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-normal text-[var(--oh-text-secondary)] bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded px-1.5 py-0.5"
                    title="Statut et dates importés automatiquement depuis le portail ANAH — non modifiables manuellement"
                    data-testid="anah-sync-readonly-hint"
                  >
                    <RefreshCw className="w-3 h-3" /> Synchronisé ANAH · lecture seule
                  </span>
                </CardTitle>
                <Badge className={`${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).bg} ${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).text} ${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).border} border text-sm px-3 py-1`}>
                  {STATUT_LABELS[anahData?.statut_normalise] || anahData?.statut_anah_label || "Inconnu"}
                </Badge>
              </div>
              <CardDescription className="text-[var(--oh-text-secondary)]">
                Dernière synchronisation : {formatDate(anahData?.last_sync_at)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Références */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Hash className="w-3 h-3" />
                    Réf. ANAH
                  </div>
                  <p className="text-[var(--oh-text)] font-mono text-lg">
                    {anahData?.reference_anah || anahStatus?.anah_reference}
                  </p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Hash className="w-3 h-3" />
                    Réf. OPAL
                  </div>
                  <p className="text-[var(--oh-text)] font-mono text-lg">
                    {anahData?.reference_opal || "-"}
                  </p>
                </div>
              </div>

              {/* Statut actuel */}
              <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4">
                <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-2">Statut actuel</div>
                <Badge className={`${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).bg} ${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).text} ${(STATUT_COLORS[anahData?.statut_normalise] || STATUT_COLORS.inconnu).border} border text-base px-4 py-2`}>
                  {anahData?.statut_anah_label || STATUT_LABELS[anahData?.statut_normalise] || "Inconnu"}
                </Badge>
              </div>
              
              {/* Informations demandeur */}
              <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4">
                <h4 className="text-sm font-medium text-[var(--oh-text-secondary)] mb-3 uppercase tracking-wider">
                  Demandeur (Bénéficiaire)
                </h4>
                <div className="space-y-2">
                  <p className="text-lg font-medium text-[var(--oh-text)] flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                    {anahData?.nom_client || "-"}
                  </p>
                  <p className="text-[var(--oh-text-secondary)] text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[var(--oh-text-secondary)]" />
                    {anahData?.adresse || "-"}
                  </p>
                </div>
              </div>
              
              {/* Informations dossier - Grille complète */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Date de création ANAH — rapatriée du bloc « Dossier ANAH » (source
                    export ANAH : anah_data.date_creation_anah). */}
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Calendar className="w-3 h-3" />
                    Date de création ANAH
                  </div>
                  <p className="text-[var(--oh-text)] font-medium">{anahData?.date_creation_anah || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Calendar className="w-3 h-3" />
                    Date de dépôt ANAH
                  </div>
                  <p className="text-[var(--oh-text)] font-medium">{anahData?.date_depot || "-"}</p>
                </div>
                {/* Date d'acceptation — champ manuel (aides_mpa_manual_data), rapatrié. */}
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Calendar className="w-3 h-3" />
                    Date d'acceptation
                  </div>
                  <p className="text-[var(--oh-text)] font-medium">{anahStatus?.manual_data?.date_acceptation || anahData?.manual_data?.date_acceptation || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Calendar className="w-3 h-3" />
                    Dernière MAJ ANAH
                  </div>
                  <p className="text-[var(--oh-text)] text-sm">{anahData?.date_mise_a_jour || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Dispositif</div>
                  <p className="text-[var(--oh-text)]">{anahData?.aide_type || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Mandataire</div>
                  <p className="text-[var(--oh-text)] text-sm">{anahData?.mandataire || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Opérateur</div>
                  <p className="text-[var(--oh-text)] text-sm">{anahData?.accompagnateur || "-"}</p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Euro className="w-3 h-3" />
                    Subvention accordée
                  </div>
                  <p className={`font-medium ${anahData?.subvention_accordee ? 'text-[var(--oh-success-text)]' : 'text-[var(--oh-text-secondary)]'}`}>
                    {anahData?.subvention_accordee 
                      ? `${anahData.subvention_accordee.toLocaleString('fr-FR')} €`
                      : "Non renseigné"
                    }
                  </p>
                </div>
                <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[var(--oh-text-secondary)] text-xs uppercase mb-1">
                    <Phone className="w-3 h-3" />
                    Téléphone
                  </div>
                  <p className="text-[var(--oh-text)] text-sm">{anahData?.contact_telephone || "Non renseigné"}</p>
                </div>
              </div>

              {/* Section éditable - Informations complémentaires */}
              <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 border border-[var(--oh-border)]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-[var(--oh-text-secondary)] flex items-center gap-2">
                    <Edit3 className="w-4 h-4" />
                    Informations complémentaires
                  </h4>
                  {!readOnly && !isEditing && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditing(true);
                        setEditData({
                          subvention_manuelle: anahData?.manual_data?.subvention_manuelle || '',
                          telephone_manuel: anahData?.manual_data?.telephone || '',
                          notes: anahData?.manual_data?.notes || '',
                          date_depot: anahData?.manual_data?.date_depot || anahData?.date_depot || '',
                          date_acceptation: anahData?.manual_data?.date_acceptation || '',
                          date_instruction: anahData?.manual_data?.date_instruction || '',
                          date_fin_travaux: anahData?.manual_data?.date_fin_travaux || '',
                          date_paiement: anahData?.manual_data?.date_paiement || ''
                        });
                      }}
                      className="border-[var(--oh-border)] text-[var(--oh-text-secondary)]"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Date dépôt</Label>
                        <Input
                          type="date"
                          value={editData.date_depot}
                          onChange={(e) => setEditData({...editData, date_depot: e.target.value})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Date acceptation</Label>
                        <Input
                          type="date"
                          value={editData.date_acceptation}
                          onChange={(e) => setEditData({...editData, date_acceptation: e.target.value})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Date instruction</Label>
                        <Input
                          type="date"
                          value={editData.date_instruction}
                          onChange={(e) => setEditData({...editData, date_instruction: e.target.value})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Date fin travaux</Label>
                        <Input
                          type="date"
                          value={editData.date_fin_travaux}
                          onChange={(e) => setEditData({...editData, date_fin_travaux: e.target.value})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Date paiement</Label>
                        <Input
                          type="date"
                          value={editData.date_paiement}
                          onChange={(e) => setEditData({...editData, date_paiement: e.target.value})}
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Subvention (saisie manuelle)</Label>
                        <Input
                          type="number"
                          value={editData.subvention_manuelle}
                          onChange={(e) => setEditData({...editData, subvention_manuelle: e.target.value})}
                          placeholder="Ex: 15000"
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-[var(--oh-text-secondary)] text-sm">Téléphone</Label>
                        <Input
                          type="tel"
                          value={editData.telephone_manuel}
                          onChange={(e) => setEditData({...editData, telephone_manuel: e.target.value})}
                          placeholder="Ex: 06 12 34 56 78"
                          className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Notes</Label>
                      <textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({...editData, notes: e.target.value})}
                        placeholder="Notes complémentaires..."
                        rows={3}
                        className="w-full bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-md px-3 py-2 text-[var(--oh-text)] mt-1 resize-none"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditing(false)}
                        className="border-[var(--oh-border)]"
                      >
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveManualData}
                        disabled={saving}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Subvention (manuel)</div>
                      <p className="text-[var(--oh-text)]">
                        {anahData?.manual_data?.subvention_manuelle 
                          ? `${anahData.manual_data.subvention_manuelle.toLocaleString('fr-FR')} €`
                          : "-"
                        }
                      </p>
                    </div>
                    <div>
                      <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Téléphone (manuel)</div>
                      <p className="text-[var(--oh-text)]">{anahData?.manual_data?.telephone || "-"}</p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Notes</div>
                      <p className="text-[var(--oh-text-secondary)] text-sm whitespace-pre-wrap">
                        {anahData?.manual_data?.notes || "-"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Info module */}
          <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[var(--oh-text-secondary)] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[var(--oh-text-secondary)]">
                  <p>
                    Les données ci-dessus proviennent de la dernière synchronisation ANAH. 
                    Pour mettre à jour, lancez une synchronisation depuis le Hub Conformité → Aides ANAH.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        // Référence présente mais pas trouvée dans snapshot
        <Card className="bg-[var(--oh-info-bg)] border-[var(--oh-info-border)]">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-[var(--oh-info-text)] flex-shrink-0" />
              <div>
                <h4 className="text-lg font-medium text-[var(--oh-info-text)]">
                  Référence ANAH non trouvée
                </h4>
                <p className="text-[var(--oh-info-text)] mt-1">
                  La référence <span className="font-mono bg-[var(--oh-info-bg)] px-2 py-0.5 rounded">{anahStatus?.anah_reference}</span> est 
                  enregistrée mais n'a pas été trouvée dans les dossiers ANAH synchronisés.
                </p>
                <ul className="mt-3 text-sm text-[var(--oh-info-text)] space-y-1">
                  <li>• Vérifiez que la référence est correcte</li>
                  <li>• Le dossier peut ne pas encore être synchronisé (lancez une sync)</li>
                  <li>• Le dossier peut avoir été archivé sur le portail ANAH</li>
                </ul>
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = '/conformité/anah'}
                    className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Aller à ANAH Sync
                  </Button>
                  {!readOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowManualInput(true)}
                      className="border-[var(--oh-border)] text-[var(--oh-text-secondary)]"
                    >
                      <Hash className="w-4 h-4 mr-1" />
                      Modifier la référence
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section éditable - Informations complémentaires (toujours visible) */}
      {!isLinked && (
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="p-4">
            <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 border border-[var(--oh-border)]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-[var(--oh-text-secondary)] flex items-center gap-2">
                  <Edit3 className="w-4 h-4" />
                  Informations complémentaires
                </h4>
                {!readOnly && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        subvention_manuelle: anahStatus?.manual_data?.subvention_manuelle || '',
                        telephone_manuel: anahStatus?.manual_data?.telephone_manuel || '',
                        notes: anahStatus?.manual_data?.notes || '',
                        date_depot: anahStatus?.manual_data?.date_depot || anahStatus?.anah_data?.date_depot || '',
                        date_acceptation: anahStatus?.manual_data?.date_acceptation || '',
                        date_instruction: anahStatus?.manual_data?.date_instruction || '',
                        date_fin_travaux: anahStatus?.manual_data?.date_fin_travaux || '',
                        date_paiement: anahStatus?.manual_data?.date_paiement || ''
                      });
                    }}
                    className="border-[var(--oh-border)] text-[var(--oh-text-secondary)]"
                    data-testid="edit-complementary-info-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                )}
              </div>
              
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Date dépôt</Label>
                      <Input
                        type="date"
                        value={editData.date_depot}
                        onChange={(e) => setEditData({...editData, date_depot: e.target.value})}
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        data-testid="date-depot-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Date acceptation</Label>
                      <Input
                        type="date"
                        value={editData.date_acceptation}
                        onChange={(e) => setEditData({...editData, date_acceptation: e.target.value})}
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                        data-testid="date-acceptation-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Date instruction</Label>
                      <Input
                        type="date"
                        value={editData.date_instruction}
                        onChange={(e) => setEditData({...editData, date_instruction: e.target.value})}
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Date fin travaux</Label>
                      <Input
                        type="date"
                        value={editData.date_fin_travaux}
                        onChange={(e) => setEditData({...editData, date_fin_travaux: e.target.value})}
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Date paiement</Label>
                      <Input
                        type="date"
                        value={editData.date_paiement}
                        onChange={(e) => setEditData({...editData, date_paiement: e.target.value})}
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Subvention (saisie manuelle)</Label>
                      <Input
                        type="number"
                        value={editData.subvention_manuelle}
                        onChange={(e) => setEditData({...editData, subvention_manuelle: e.target.value})}
                        placeholder="Ex: 15000"
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[var(--oh-text-secondary)] text-sm">Téléphone</Label>
                      <Input
                        type="tel"
                        value={editData.telephone_manuel}
                        onChange={(e) => setEditData({...editData, telephone_manuel: e.target.value})}
                        placeholder="Ex: 06 12 34 56 78"
                        className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[var(--oh-text-secondary)] text-sm">Notes</Label>
                    <textarea
                      value={editData.notes}
                      onChange={(e) => setEditData({...editData, notes: e.target.value})}
                      placeholder="Notes complémentaires..."
                      rows={3}
                      className="w-full bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-md px-3 py-2 text-[var(--oh-text)] mt-1 resize-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditing(false)}
                      className="border-[var(--oh-border)]"
                    >
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveManualData}
                      disabled={saving}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="save-complementary-info-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Date dépôt</div>
                    <p className="text-[var(--oh-text)]">{anahStatus?.manual_data?.date_depot || anahStatus?.anah_data?.date_depot || "-"}</p>
                  </div>
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Date acceptation</div>
                    <p className="text-[var(--oh-text)]">{anahStatus?.manual_data?.date_acceptation || "-"}</p>
                  </div>
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Date instruction</div>
                    <p className="text-[var(--oh-text)]">{anahStatus?.manual_data?.date_instruction || "-"}</p>
                  </div>
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Date fin travaux</div>
                    <p className="text-[var(--oh-text)]">{anahStatus?.manual_data?.date_fin_travaux || "-"}</p>
                  </div>
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Date paiement</div>
                    <p className="text-[var(--oh-text)]">{anahStatus?.manual_data?.date_paiement || "-"}</p>
                  </div>
                  <div>
                    <div className="text-[var(--oh-text-secondary)] text-xs uppercase mb-1">Subvention (manuel)</div>
                    <p className="text-[var(--oh-text)]">
                      {anahStatus?.manual_data?.subvention_manuelle 
                        ? `${anahStatus.manual_data.subvention_manuelle.toLocaleString('fr-FR')} €`
                        : "-"
                      }
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de recherche */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              <Search className="w-5 h-5 text-[var(--oh-success-text)]" />
              Rechercher un dossier ANAH
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-secondary)]">
              Recherchez par nom, référence ou adresse
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-2 mt-4">
            <Input
              placeholder="Nom, référence ANAH, OPAL ou adresse..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchAnahDossiers()}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]"
              autoFocus
            />
            <Button
              onClick={searchAnahDossiers}
              disabled={searching || searchQuery.length < 2}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          
          <div className="flex-1 overflow-y-auto mt-4 space-y-2">
            {searchResults.length === 0 ? (
              <div className="text-center py-8 text-[var(--oh-text-secondary)]">
                {searching ? (
                  <p>Recherche en cours...</p>
                ) : searchQuery.length >= 2 ? (
                  <p>Aucun résultat pour "{searchQuery}"</p>
                ) : (
                  <p>Entrez au moins 2 caractères pour rechercher</p>
                )}
              </div>
            ) : (
              searchResults.map((result, idx) => {
                const colors = STATUT_COLORS[result.statut_normalise] || STATUT_COLORS.inconnu;
                return (
                  <div
                    key={idx}
                    className="p-4 bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg hover:bg-[var(--oh-bg-elevated)] cursor-pointer transition-colors"
                    onClick={() => linkAnahDossier(result.reference_anah)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[var(--oh-text)] font-medium">{result.nom_client || "Client inconnu"}</p>
                        <p className="text-[var(--oh-text-secondary)] text-sm font-mono">
                          Réf. {result.reference_anah}
                          {result.reference_opal && ` / OPAL ${result.reference_opal}`}
                        </p>
                        {result.adresse && (
                          <p className="text-[var(--oh-text-secondary)] text-sm mt-1">{result.adresse}</p>
                        )}
                      </div>
                      <Badge className={`${colors.bg} ${colors.text} ${colors.border} border text-xs`}>
                        {STATUT_LABELS[result.statut_normalise] || result.statut_anah_label}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs text-[var(--oh-text-secondary)]">
                      {result.aide_type && <span>{result.aide_type}</span>}
                      {result.date_depot && <span>Déposé le {formatDateFR(result.date_depot)}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {linking && (
            <div className="absolute inset-0 bg-[var(--oh-bg-surface)] flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--oh-success-text)]" />
              <span className="ml-3 text-[var(--oh-text)]">Liaison en cours...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal saisie manuelle */}
      <Dialog open={showManualInput} onOpenChange={setShowManualInput}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              <Hash className="w-5 h-5 text-[var(--oh-success-text)]" />
              Saisir la référence ANAH
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-secondary)]">
              Entrez manuellement le numéro de dossier ANAH
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-4">
            <div>
              <Label className="text-[var(--oh-text-secondary)]">Référence dossier ANAH</Label>
              <Input
                placeholder="Ex: 1085302"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono mt-1"
                autoFocus
              />
              <p className="text-xs text-[var(--oh-text-secondary)] mt-1">
                Numéro à 7 chiffres visible sur le portail Mon Projet ANAH
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowManualInput(false)}
                className="border-[var(--oh-border)]"
              >
                Annuler
              </Button>
              <Button
                onClick={() => linkAnahDossier(manualRef)}
                disabled={!manualRef || manualRef.length < 5 || linking}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {linking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link className="w-4 h-4 mr-1" />}
                Lier
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
